import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDeleteGlow } from '../../hooks/useGlows'
import { Input } from '../../components/ui/Input'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { ErrorBanner } from '../../components/ErrorBanner'
import { ApiError, apiErrorMessage } from '../../api/client'
import type { Glow } from '../../api/types'

// DeleteGlowDialog is the type-to-confirm modal for the irreversible
// (soft) delete. Split out of SettingsTab (FE-4) so the settings file
// stays focused on field editing. Delete soft-deletes the glow
// (DELETE /v1/glows/{id}): it stops serving immediately and frees a slot;
// files + data are kept 30 days then purged, and the name stays reserved
// until then. The owner must type the glow's `name` (shown as "Beacon")
// exactly to enable the action — stricter than a plain confirm because
// there is no undo.
export function DeleteGlowDialog({
  glow,
  onClose,
}: {
  glow: Glow
  onClose: () => void
}) {
  const del = useDeleteGlow(glow.id)
  const navigate = useNavigate()
  const [typed, setTyped] = useState('')
  const [error, setError] = useState<string | null>(null)

  // The destructive action only enables on an exact-name match (Beacon).
  const matches = typed === glow.name

  const onConfirm = async () => {
    if (!matches) return
    setError(null)
    try {
      // useDeleteGlow navigates to /glows on success.
      await del.mutateAsync()
    } catch (e) {
      // A `not_found` means it's already gone (a prior delete or a race) —
      // the user's intent is satisfied, so send them to the list too.
      if (e instanceof ApiError && e.code === 'not_found') {
        onClose()
        navigate('/glows')
        return
      }
      setError(apiErrorMessage(e, 'Could not delete glow.'))
    }
  }

  return (
    <Modal onClose={onClose} labelledBy="delete-glow-title">
      <h3 id="delete-glow-title" className="text-lg font-semibold">
        Delete this glow?
      </h3>
      <p className="mt-2 text-sm text-cream/70">
        This stops serving the glow immediately and frees a glow slot.
        Files and data are kept for 30 days, then permanently deleted. The
        subdomain name stays reserved until then. This cannot be undone.
      </p>
      <p className="mt-3 text-sm text-cream/70">
        Type the glow&apos;s Beacon{' '}
        <code className="rounded bg-surface-2 px-1 font-mono text-xs">
          {glow.name}
        </code>{' '}
        to confirm.
      </p>
      <Input
        type="text"
        className="mt-2"
        value={typed}
        autoFocus
        placeholder={glow.name}
        disabled={del.isPending}
        onChange={(e) => setTyped(e.target.value)}
      />
      {error && <ErrorBanner className="mt-3">{error}</ErrorBanner>}
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={del.isPending}>
          Cancel
        </Button>
        <Button
          variant="danger"
          onClick={onConfirm}
          disabled={!matches || del.isPending}
        >
          {del.isPending ? 'Deleting…' : 'Delete glow'}
        </Button>
      </div>
    </Modal>
  )
}
